import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Bars3Icon, XMarkIcon, ArrowRightIcon } from "@heroicons/react/24/outline";
import ThemeToggle from "../../components/ThemeToggle";

const NAV = [
  { name: "Home",     to: "/" },
  { name: "About",    to: "/about" },
  { name: "Programs", to: "/programs" },
  { name: "Notice",   to: "/notice" },
  { name: "Gallery",  to: "/gallery" },
  { name: "Articles", to: "/articles" },
  { name: "FAQs",     to: "/faqs" },
  { name: "Contact",  to: "/contact" },
];

/**
 * The logo is drawn for white paper — its navy house and blue script vanish on
 * the dark theme's near-black ground. It always sits on its own white plate so
 * the brand ink stays true in both themes; the hairline border keeps the plate
 * from reading as a stray white box on the light theme's white bar.
 */
function LogoPlate({ className = "h-9" }) {
  return (
    <span className="inline-flex items-center justify-center rounded-md bg-white p-1.5 border border-subtle">
      <img src="/img/logo.png" alt="Whitehouse Education Zone" className={`${className} w-auto`} />
    </span>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen]         = useState(false);
  const { pathname }            = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 32);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const isActive = (to) => pathname === to || (to === "/" && pathname === "/");

  return (
    <>
      {/* The bar always reserves its border, so scrolling never changes its height.
          This is the only backdrop-blur in the app — blur is expensive on mobile. */}
      <header
        className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,border-color,box-shadow] duration-base ease-standard ${
          scrolled
            ? "bg-bg/85 backdrop-blur-md border-subtle shadow-sm"
            : "bg-bg border-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">

          {/* ── Logo ── */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0 group">
            <span className="transition-transform duration-base ease-standard group-hover:scale-105">
              <LogoPlate />
            </span>
            <div className="hidden sm:block leading-none">
              <span className="block text-[15px] font-bold text-fg tracking-tight">
                Whitehouse
              </span>
              <span className="block text-[10px] font-semibold text-brand-text tracking-[0.18em] uppercase mt-0.5">
                Education Zone
              </span>
            </div>
          </Link>

          {/* ── Desktop Nav ── */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV.map((item) => (
              <Link
                key={item.name}
                to={item.to}
                className={`relative px-4 py-2 text-sm font-medium transition-colors duration-fast ease-standard ${
                  isActive(item.to)
                    ? "text-primary-text"
                    : "text-fg-secondary hover:text-fg"
                }`}
              >
                {item.name}
                {isActive(item.to) && (
                  <motion.div
                    layoutId="nav-pill"
                    className="absolute bottom-0 left-4 right-4 h-px bg-primary rounded-full"
                  />
                )}
              </Link>
            ))}
          </div>

          {/* ── Right actions ── */}
          <div className="flex items-center gap-2 sm:gap-3">
            <Link
              to="/register"
              className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-semibold text-on-primary bg-primary hover:bg-primary-hover transition-colors duration-base ease-standard shadow-sm"
            >
              Apply Now
              <ArrowRightIcon className="w-3.5 h-3.5" />
            </Link>

            <ThemeToggle />

            <button
              onClick={() => setOpen(!open)}
              className="lg:hidden p-2 rounded-md text-fg-secondary hover:text-fg transition-colors duration-fast ease-standard"
              aria-label="Toggle menu"
            >
              {open ? <XMarkIcon className="w-6 h-6" /> : <Bars3Icon className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </header>

      {/* ── Mobile Drawer ── */}
      <AnimatePresence>
        {open && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-neutral-950/50 lg:hidden"
              onClick={() => setOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="fixed right-0 top-0 bottom-0 z-50 w-[280px] bg-bg border-l border-subtle flex flex-col lg:hidden"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-5 py-4 border-b border-subtle">
                <div className="flex items-center gap-2.5">
                  <LogoPlate className="h-8" />
                  <span className="text-fg font-bold text-sm">Whitehouse</span>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  className="text-fg-muted hover:text-fg p-1 rounded-md transition-colors duration-fast ease-standard"
                  aria-label="Close menu"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Links */}
              <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-0.5">
                {NAV.map((item) => (
                  <Link
                    key={item.name}
                    to={item.to}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-fast ease-standard ${
                      isActive(item.to)
                        ? "bg-primary-subtle text-primary-text"
                        : "text-fg-secondary hover:text-fg hover:bg-bg-muted"
                    }`}
                  >
                    {item.name}
                    {isActive(item.to) && <ArrowRightIcon className="w-3.5 h-3.5 text-primary-text" />}
                  </Link>
                ))}
              </nav>

              {/* Footer */}
              <div className="p-4 border-t border-subtle space-y-3">
                <Link
                  to="/register"
                  className="flex items-center justify-center gap-2 w-full py-3 rounded-lg font-semibold text-sm text-on-primary bg-primary hover:bg-primary-hover transition-colors duration-base ease-standard"
                >
                  Apply Now <ArrowRightIcon className="w-4 h-4" />
                </Link>

                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-fg-muted">Appearance</span>
                  <ThemeToggle />
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
